/**
 * WorkspacePill — persistent-chrome workspace identity + switcher
 * (PLAN-multi-org-customer-journey-20260816.md D4 / §10, Phase 3).
 *
 * The org pill renders on every authenticated page: the ACTIVE workspace's
 * name with a deterministic color mark (invariant 3 — two orgs must never
 * look alike at a glance), the user's native org additionally marked
 * "yours". Clicking opens the switcher: one row per workspace, role hint,
 * active check; one click switches.
 *
 * ITS BEHAVIOUR IS SHARED (design-system §7b rule 1, 2026-09-30): which
 * workspaces, which one THIS TAB is in (never a guess), the sealed switch,
 * setting the personal workspace aside and starting a new organization all
 * live in src/hooks/useWorkspaceSwitcher.ts, and the words in
 * ./workspacePillWords.ts; the new design's app header reads the same two.
 * This file keeps the markup, its test ids, and opening and closing.
 *
 * Tolerance contract: any load failure renders NOTHING (the header must
 * never break over workspace listing). Labels go through `workspaceLabel` —
 * never a raw account id.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useWorkspaceSwitcher } from '../hooks/useWorkspaceSwitcher';
import { WORKSPACE_PILL_WORDS as W } from './workspacePillWords';

const PillButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 220px;
  padding: 5px 12px;
  margin-right: 10px;
  border: 1px solid var(--color-border-dark, rgba(127, 127, 127, 0.35));
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.82rem;
  cursor: pointer;
  white-space: nowrap;
  &:hover { border-color: var(--color-accent, rgba(127, 127, 127, 0.7)); }
  &:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
`;

const Dot = styled.span<{ $color: string }>`
  width: 9px;
  height: 9px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: ${(p) => p.$color};
  /* Definition ring so the mark holds contrast on light chrome. */
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.15);
`;

const PillName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const YoursTag = styled.span`
  flex: 0 0 auto;
  font-size: 0.68rem;
  opacity: 0.9;
  border: 1px dashed var(--color-accent, rgba(127, 127, 127, 0.6));
  border-radius: 999px;
  padding: 1px 6px;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 260px;
  max-width: 320px;
  border: 1px solid var(--color-border-light, rgba(127, 127, 127, 0.3));
  border-radius: 10px;
  background: var(--color-background-primary, var(--surface, #fff));
  color: inherit;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
  padding: 6px;
  z-index: 1200;
`;

const MenuWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const Row = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.85rem;
  text-align: left;
  cursor: ${(p) => (p.$active ? 'default' : 'pointer')};
  opacity: ${(p) => (p.$active ? 1 : 0.92)};
  &:hover { background: var(--color-background-hover, rgba(127, 127, 127, 0.12)); }
  &:focus-visible { outline: 2px solid currentColor; outline-offset: -2px; }
`;

const RowName = styled.span`
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowHint = styled.span`
  flex: 0 0 auto;
  font-size: 0.7rem;
  opacity: 0.65;
`;

const WorkspacePill: React.FC = () => {
  const sw = useWorkspaceSwitcher();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!sw.ready) return null;
  const { active, setAsideRow } = sw;

  return (
    <MenuWrap ref={wrapRef}>
      <PillButton
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={active ? W.pillLabel(active.label) : W.pillLabelResolving}
        onClick={() => setOpen((v) => !v)}
        data-testid="workspace-pill"
      >
        {active ? (
          <>
            <Dot $color={active.markColor} aria-hidden="true" />
            <PillName>{active.label}</PillName>
            {active.yours && <YoursTag>{W.yours}</YoursTag>}
          </>
        ) : (
          /* Neutral resolving state — honest over a guessed org. */
          <PillName>{W.resolving}</PillName>
        )}
      </PillButton>
      {open && (
        <Menu role="menu" aria-label={W.menuLabel}>
          {/* No "organization details…" row (owner directive 2026-08-16 #2:
              redundant — the /organization page opens on the ACTIVE workspace
              and is reachable from the profile dropdown). The pill switches
              workspaces — plus the one creation affordance the 2026-08-18
              owner directive added: "start a new organization" (task #75). */}
          {sw.rows.map((ws) => (
            <Row
              key={ws.accountId}
              type="button"
              role="menuitem"
              $active={ws.current}
              aria-current={ws.current ? 'true' : undefined}
              disabled={sw.switching}
              onClick={() => sw.switchTo(ws)}
              data-testid={`workspace-row-${ws.accountId}`}
            >
              <Dot $color={ws.markColor} aria-hidden="true" />
              <RowName>{ws.label}</RowName>
              {/* "yours" = VERIFIED ownership — founded it or was made the
                  owner later (owner directive 2026-08-16: a user can own
                  multiple workspaces and every one of them says so). */}
              {ws.yours && <YoursTag>{W.yours}</YoursTag>}
              <RowHint>{ws.hint}</RowHint>
            </Row>
          ))}
          {setAsideRow && (
            <Row
              type="button"
              role="menuitem"
              disabled={sw.settingAside || sw.switching}
              onClick={() => {
                void sw.setAside(setAsideRow).then((outcome) => {
                  // The platform would re-activate it (not in lazy mode): nothing changed, so the menu closes.
                  if (outcome === 'eager-mode') setOpen(false);
                });
              }}
              data-testid="workspace-set-aside"
            >
              <RowName>
                {sw.settingAside ? W.settingAside : W.setAside(setAsideRow.label)}
              </RowName>
              <RowHint>{W.nothingDeleted}</RowHint>
            </Row>
          )}
          <Row
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              sw.startNewOrganization();
            }}
            data-testid="workspace-create-org"
            style={{ borderTop: '1px solid var(--color-border-light, rgba(127,127,127,0.25))', borderRadius: 0, marginTop: '4px', paddingTop: '10px' }}
          >
            <RowName>{W.newOrganization}</RowName>
          </Row>
        </Menu>
      )}
    </MenuWrap>
  );
};

export default WorkspacePill;
